import { type ComponentType, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import { Label } from "@v2/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@v2/components/ui/select";
import { EmptyState, SectionLoading } from "../../components/state";
import { isFounderRole } from "../../lib/labels";
import { supabaseV2 } from "../../lib/supabase";
import { useWorkspaceData } from "../../lib/workspace-data";
import type { SquadScoreSetting, Team } from "../../types";
import { AccountCard } from "../account/AccountCard";
import { MyTeamsCard } from "../account/MyTeamsCard";
import { SupportSection } from "../support/SupportSection";
import { TeamInviteLinkCard } from "../admin/TeamInviteLinkCard";
import { CompetitionsManager } from "./CompetitionsManager";
import { OpponentsManager } from "./OpponentsManager";
import { PushSubscribeManager } from "./PushSubscribeManager";
import {
  SquadScorePreview,
  SquadScoreSettingsForm,
} from "./SquadScoreSettingsForm";
import { SquadScoreGuideButton } from "./SquadScoreGuide";

// FIXME: TeamIdentityCard (App.tsx riga 5537) e TeamMembersManager (App.tsx riga 5960)
// sono ancora nel monolite. Iniettati come prop per evitare import circolari finché
// non vengono estratti in feature folder dedicati.
export type TeamIdentityCardProps = { team: Team; canEdit: boolean };
export type TeamMembersManagerProps = { teamId: string };

export function SettingsView({
  team,
  role,
  isAppAdmin,
  userId,
  userEmail,
  onCreateNewTeam,
  TeamIdentityCard,
  TeamMembersManager,
}: {
  team: Team;
  role: string;
  isAppAdmin: boolean;
  userId: string;
  userEmail: string;
  onCreateNewTeam?: () => void;
  TeamIdentityCard: ComponentType<TeamIdentityCardProps>;
  TeamMembersManager: ComponentType<TeamMembersManagerProps>;
}) {
  const { t } = useTranslation("settings");
  const canManageMembers = isFounderRole(role) || isAppAdmin;
  const queryClient = useQueryClient();
  const { players, attendance: responses, matchStats, isLoading } = useWorkspaceData(team.id);
  const settingsQuery = useQuery({
    queryKey: ["squad-score-settings", team.id],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("squad_score_settings")
        .select("*")
        .eq("team_id", team.id)
        .order("is_active", { ascending: false })
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return (data || []) as SquadScoreSetting[];
    },
  });
  const activeSetting = (settingsQuery.data || []).find((s) => s.is_active) || settingsQuery.data?.[0] || null;
  const updateSetting = useMutation({
    mutationFn: async (payload: SquadScoreSetting) => {
      const fields = {
        name: payload.name,
        training_present_points: payload.training_present_points,
        match_day_present_points: payload.match_day_present_points,
        late_penalty: payload.late_penalty,
        absence_penalty: payload.absence_penalty,
        no_response_penalty: payload.no_response_penalty,
        goal_points: payload.goal_points,
        assist_points: payload.assist_points,
        yellow_card_penalty: payload.yellow_card_penalty,
        red_card_penalty: payload.red_card_penalty,
        win_bonus: payload.win_bonus,
        draw_points: payload.draw_points,
        loss_penalty: payload.loss_penalty,
        decay_half_life_days: payload.decay_half_life_days,
        min_events: payload.min_events,
        is_active: payload.is_active,
      };
      const { error } = await supabaseV2
        .from("squad_score_settings")
        .update(fields)
        .eq("id", payload.id)
        .eq("team_id", payload.team_id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["squad-score-settings", team.id] }),
  });

  const location = useLocation();
  useEffect(() => {
    const hash = location.hash.replace(/^#/, "");
    if (!hash) return;
    const scroll = () => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    const raf = requestAnimationFrame(scroll);
    return () => cancelAnimationFrame(raf);
  }, [location.hash, isLoading]);

  return (
    <div className="space-y-6 animate-fade-in">
      <p className="text-muted-foreground">{t("subtitle")}</p>

      <section id="team" className="scroll-mt-24">
        <TeamIdentityCard team={team} canEdit={isFounderRole(role) || isAppAdmin} />
      </section>

      <section id="squad-score" className="grid lg:grid-cols-2 gap-4 scroll-mt-24">
        <Card>
          <CardHeader className="pb-3 flex flex-row items-start justify-between gap-3 flex-wrap">
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <CardTitle className="text-lg">{t("squadScore.title")}</CardTitle>
                <SquadScoreGuideButton />
              </div>
              <CardDescription>{t("squadScore.description")}</CardDescription>
            </div>
            {(settingsQuery.data || []).length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs">{t("squadScore.activePreset")}</Label>
                <Select
                  value={activeSetting?.id || ""}
                  onValueChange={(id) => {
                    const target = (settingsQuery.data || []).find((s) => s.id === id);
                    if (!target || target.is_active) return;
                    updateSetting.mutate({ ...target, is_active: true });
                  }}
                >
                  <SelectTrigger className="h-8 text-sm w-[220px]">
                    <SelectValue placeholder={t("squadScore.selectPreset")} />
                  </SelectTrigger>
                  <SelectContent>
                    {(settingsQuery.data || []).map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name} · {t("squadScore.presetMin")} {s.min_events} · {t("squadScore.presetGoals")} {s.goal_points} / {t("squadScore.presetAssists")} {s.assist_points}
                        {s.is_active ? t("squadScore.active") : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {activeSetting ? (
              <SquadScoreSettingsForm
                setting={activeSetting}
                isSaving={updateSetting.isPending}
                onSave={(setting) => updateSetting.mutate(setting)}
              />
            ) : (
              <EmptyState text={t("squadScore.noActive")} />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{t("squadScore.previewTitle")}</CardTitle>
            <CardDescription>{t("squadScore.previewDesc")}</CardDescription>
          </CardHeader>
          <CardContent>
            {activeSetting && !isLoading ? (
              <SquadScorePreview players={players} responses={responses} matchStats={matchStats} setting={activeSetting} />
            ) : (
              <SectionLoading />
            )}
          </CardContent>
        </Card>
      </section>

      <section id="competitions" className="grid grid-cols-1 lg:grid-cols-2 gap-4 scroll-mt-24">
        <OpponentsManager teamId={team.id} />
        <CompetitionsManager teamId={team.id} />
      </section>

      <section id="notifications" className="scroll-mt-24">
        <PushSubscribeManager />
      </section>

      <section id="account" className="grid lg:grid-cols-2 gap-4 scroll-mt-24">
        <AccountCard currentEmail={userEmail} />
        <MyTeamsCard userId={userId} onCreateNewTeam={onCreateNewTeam} />
      </section>

      <section id="support" className="scroll-mt-24">
        <SupportSection activeTeamId={team.id} />
      </section>

      {canManageMembers && (
        <section id="members" className="space-y-4 scroll-mt-24">
          <TeamInviteLinkCard teamId={team.id} teamName={team.name} />
          <TeamMembersManager teamId={team.id} />
        </section>
      )}
    </div>
  );
}
